import { useState, useCallback } from 'react';
import { api, formatCurrency } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast, Toaster } from 'sonner';
import QrScannerModal from '../components/QrScannerModal';
import { Camera, Check, Gift, Search, AlertTriangle, XCircle, Clock, Plus, X } from 'lucide-react';

const STATUT_INFO = {
  CREE: { icon: Clock, color: 'text-blue-400', bg: 'bg-blue-400/15', label: 'Créé' },
  EN_ATTENTE: { icon: Clock, color: 'text-orange-400', bg: 'bg-orange-400/15', label: 'En attente' },
  UTILISE: { icon: Check, color: 'text-green-400', bg: 'bg-green-400/15', label: 'Déjà utilisé' },
  EXPIRE: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-400/15', label: 'Expiré' },
};

export default function ResponsablePage() {
  const [tab, setTab] = useState('creer'); // 'creer' | 'scanner'
  const [showScanner, setShowScanner] = useState(false);

  // Scanner state
  const [bon, setBon] = useState(null);
  const [manualCode, setManualCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [result, setResult] = useState(null);

  // Create form state
  const [form, setForm] = useState({ montant: '', client_nom: '', client_email: '', beneficiaire: '', message: '' });
  const [saving, setSaving] = useState(false);
  const [createdBon, setCreatedBon] = useState(null);

  // ========= CREATE =========
  const submitCreate = async () => {
    if (!form.montant || parseFloat(form.montant) <= 0) { toast.error('Montant requis'); return; }
    setSaving(true);
    try {
      const res = await api.post('/bons-cadeaux', {
        montant: parseFloat(form.montant),
        client_nom: form.client_nom,
        client_email: form.client_email,
        beneficiaire: form.beneficiaire,
        message: form.message,
      });
      if (res.data.error) { toast.error(res.data.error); setSaving(false); return; }
      toast.success(`Bon ${res.data.numero} créé — ${res.data.montant}€`);
      setCreatedBon(res.data);
      setForm({ montant: '', client_nom: '', client_email: '', beneficiaire: '', message: '' });
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const downloadPdf = async (b) => {
    try {
      toast.info('Génération du PDF...');
      const res = await api.get(`/bons-cadeaux/${b.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `bon-cadeau-${b.numero}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF téléchargé');
    } catch { toast.error('Erreur PDF'); }
  };

  // ========= SCAN =========
  const lookupBon = useCallback(async (code) => {
    if (!code.trim()) return;
    setLoading(true);
    setBon(null);
    setResult(null);
    try {
      const res = await api.get(`/bons-cadeaux/scan/${encodeURIComponent(code.trim())}`);
      setBon(res.data);
    } catch {
      toast.error('Bon non trouvé');
      setBon(null);
    }
    setLoading(false);
  }, []);

  const handleScan = useCallback((decodedText) => {
    setShowScanner(false);
    setManualCode(decodedText);
    lookupBon(decodedText);
  }, [lookupBon]);

  const handleValidate = async () => {
    if (!bon) return;
    setValidating(true);
    try {
      const res = await api.put(`/bons-cadeaux/${bon.id}/valider`);
      if (res.data.error) {
        setResult({ success: false, message: res.data.error });
        toast.error(res.data.error);
      } else {
        setResult({ success: true, message: res.data.message });
        toast.success(res.data.message);
        setBon({ ...bon, statut: 'UTILISE' });
      }
    } catch {
      setResult({ success: false, message: 'Erreur lors de la validation' });
      toast.error('Erreur');
    }
    setValidating(false);
  };

  const handleRemettre = async () => {
    if (!bon) return;
    try {
      const res = await api.put(`/bons-cadeaux/${bon.id}/remettre`);
      if (res.data.error) { toast.error(res.data.error); return; }
      toast.success(res.data.message);
      setBon({ ...bon, statut: 'EN_ATTENTE' });
    } catch { toast.error('Erreur'); }
  };

  const resetScan = () => {
    setBon(null);
    setResult(null);
    setManualCode('');
  };

  const canValidate = bon && (bon.statut === 'CREE' || bon.statut === 'EN_ATTENTE');
  const statutInfo = bon ? STATUT_INFO[bon.statut] || STATUT_INFO.CREE : null;

  return (
    <div className="min-h-screen bg-[#0A0F1E] flex flex-col items-center px-4 py-6" data-testid="responsable-page">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-[#C9A96E]/20 to-[#C9A96E]/5 flex items-center justify-center mb-3 border border-[#C9A96E]/20">
          <Gift size={24} className="text-[#C9A96E]" />
        </div>
        <h1 className="text-xl font-semibold text-white tracking-tight">Le Mas</h1>
        <p className="text-xs text-[#8892A8] mt-1">Responsable — Bons Cadeaux</p>
      </div>

      {/* Tabs */}
      <div className="flex w-full max-w-sm bg-[#111827] rounded-xl p-1 mb-6 border border-[#1E2740]" data-testid="resp-tabs">
        <button
          onClick={() => setTab('creer')}
          className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === 'creer' ? 'bg-[#C9A96E] text-[#0A0F1E]' : 'text-[#5A6478] hover:text-white'}`}
          data-testid="tab-creer"
        >
          <Plus size={14} className="inline mr-1.5 -mt-0.5" /> Créer
        </button>
        <button
          onClick={() => { setTab('scanner'); resetScan(); }}
          className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === 'scanner' ? 'bg-[#C9A96E] text-[#0A0F1E]' : 'text-[#5A6478] hover:text-white'}`}
          data-testid="tab-scanner"
        >
          <Camera size={14} className="inline mr-1.5 -mt-0.5" /> Scanner
        </button>
      </div>

      {/* ========= TAB CREER ========= */}
      {tab === 'creer' && (
        <div className="w-full max-w-sm space-y-4" data-testid="create-section">
          {!createdBon ? (
            <div className="bg-[#111827] rounded-2xl border border-[#1E2740] p-4 space-y-3">
              <div>
                <label className="text-[10px] text-[#5A6478] uppercase tracking-wider block mb-1">Montant *</label>
                <div className="relative">
                  <Input type="number" step="0.01" value={form.montant} onChange={e => setForm(f => ({ ...f, montant: e.target.value }))}
                    className="bg-[#0A0F1E] border-[#1E2740] text-white font-mono text-lg pr-8" placeholder="50.00" autoFocus data-testid="resp-montant" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#C9A96E] font-mono">€</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-[#5A6478] uppercase tracking-wider block mb-1">Client (acheteur)</label>
                  <Input value={form.client_nom} onChange={e => setForm(f => ({ ...f, client_nom: e.target.value }))}
                    className="bg-[#0A0F1E] border-[#1E2740] text-white" placeholder="Nom" data-testid="resp-client" />
                </div>
                <div>
                  <label className="text-[10px] text-[#5A6478] uppercase tracking-wider block mb-1">Email</label>
                  <Input type="email" value={form.client_email} onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))}
                    className="bg-[#0A0F1E] border-[#1E2740] text-white" placeholder="email@..." data-testid="resp-email" />
                </div>
              </div>
              <div>
                <label className="text-[10px] text-[#5A6478] uppercase tracking-wider block mb-1">Bénéficiaire</label>
                <Input value={form.beneficiaire} onChange={e => setForm(f => ({ ...f, beneficiaire: e.target.value }))}
                  className="bg-[#0A0F1E] border-[#1E2740] text-white" placeholder="Qui va l'utiliser ?" data-testid="resp-beneficiaire" />
              </div>
              <div>
                <label className="text-[10px] text-[#5A6478] uppercase tracking-wider block mb-1">Message personnalisé</label>
                <Input value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  className="bg-[#0A0F1E] border-[#1E2740] text-white" placeholder="Bon anniversaire !" data-testid="resp-message" />
              </div>
              <Button onClick={submitCreate} disabled={saving}
                className="w-full h-12 bg-[#C9A96E] text-[#0A0F1E] hover:bg-[#C9A96E]/90 rounded-xl text-base font-medium"
                data-testid="resp-create-btn">
                <Gift size={18} className="mr-2" /> {saving ? 'Création...' : 'Créer le bon cadeau'}
              </Button>
            </div>
          ) : (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-300" data-testid="created-result">
              <div className="bg-[#111827] rounded-2xl border border-green-500/30 p-5 text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-green-500/20 flex items-center justify-center mb-3">
                  <Check size={24} className="text-green-400" />
                </div>
                <p className="text-green-400 font-medium text-sm mb-1">Bon cadeau créé !</p>
                <p className="text-4xl font-bold text-[#C9A96E] font-mono mt-2">{createdBon.montant}€</p>
                <p className="text-xs font-mono text-[#5A6478] mt-1">{createdBon.numero}</p>
                {createdBon.beneficiaire && <p className="text-sm text-white mt-2">Pour : {createdBon.beneficiaire}</p>}
                {createdBon.client_nom && <p className="text-xs text-[#8892A8]">De : {createdBon.client_nom}</p>}
              </div>
              <Button onClick={() => downloadPdf(createdBon)}
                className="w-full h-11 bg-[#1E2740] text-[#C9A96E] hover:bg-[#263050] rounded-xl"
                data-testid="resp-download-pdf">
                <Gift size={16} className="mr-2" /> Télécharger le PDF
              </Button>
              <Button onClick={() => setCreatedBon(null)} variant="ghost"
                className="w-full text-[#5A6478] hover:text-white text-sm"
                data-testid="resp-create-another">
                <Plus size={16} className="mr-2" /> Créer un autre bon
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ========= TAB SCANNER ========= */}
      {tab === 'scanner' && (
        <div className="w-full max-w-sm space-y-4">
          {!bon && (
            <>
              <Button onClick={() => setShowScanner(true)}
                className="w-full h-14 bg-[#C9A96E] text-[#0A0F1E] hover:bg-[#C9A96E]/90 rounded-xl text-base font-medium"
                data-testid="resp-open-scanner">
                <Camera size={20} className="mr-2" /> Scanner le QR Code
              </Button>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-[#1E2740]" />
                <span className="text-[10px] text-[#5A6478] uppercase tracking-widest">ou</span>
                <div className="flex-1 h-px bg-[#1E2740]" />
              </div>

              <div className="flex gap-2">
                <Input value={manualCode} onChange={e => setManualCode(e.target.value)}
                  placeholder="N° du bon (ex: LM-202604-...)"
                  className="bg-[#111827] border-[#1E2740] text-white placeholder:text-[#3A4560]"
                  onKeyDown={e => e.key === 'Enter' && lookupBon(manualCode)}
                  data-testid="resp-manual-input" />
                <Button onClick={() => lookupBon(manualCode)} disabled={loading || !manualCode.trim()}
                  className="bg-[#1E2740] text-[#C9A96E] hover:bg-[#263050] px-4"
                  data-testid="resp-search-btn">
                  <Search size={18} />
                </Button>
              </div>
            </>
          )}

          {loading && <div className="text-[#8892A8] text-sm animate-pulse text-center">Recherche...</div>}

          {bon && (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-300" data-testid="resp-bon-result">
              <div className="bg-[#111827] rounded-2xl border border-[#1E2740] overflow-hidden">
                <div className="text-center py-6 border-b border-[#1E2740]">
                  <p className="text-5xl font-bold text-[#C9A96E] font-mono">{bon.montant}€</p>
                  <p className="text-xs text-[#5A6478] mt-2 font-mono">{bon.numero}</p>
                </div>
                <div className="px-4 py-3 flex items-center justify-center gap-2 border-b border-[#1E2740]">
                  {statutInfo && (
                    <span className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1 rounded-full ${statutInfo.bg} ${statutInfo.color}`} data-testid="resp-bon-statut">
                      <statutInfo.icon size={14} /> {statutInfo.label}
                    </span>
                  )}
                </div>
                <div className="px-4 py-3 space-y-2 text-sm">
                  {bon.beneficiaire && <div className="flex justify-between"><span className="text-[#5A6478]">Bénéficiaire</span><span className="text-white font-medium">{bon.beneficiaire}</span></div>}
                  {bon.client_nom && <div className="flex justify-between"><span className="text-[#5A6478]">Offert par</span><span className="text-[#8892A8]">{bon.client_nom}</span></div>}
                  <div className="flex justify-between"><span className="text-[#5A6478]">Expire le</span><span className="text-[#8892A8] font-mono">{bon.date_expiration}</span></div>
                  {bon.message && <p className="text-xs italic text-[#C9A96E]/70 pt-1 text-center">"{bon.message}"</p>}
                </div>
              </div>

              {result && (
                <div className={`flex items-center gap-2 p-3 rounded-xl text-sm ${result.success ? 'bg-green-400/10 text-green-400' : 'bg-red-400/10 text-red-400'}`} data-testid="resp-validation-result">
                  {result.success ? <Check size={18} /> : <AlertTriangle size={18} />}
                  <span>{result.message}</span>
                </div>
              )}

              <div className="space-y-2">
                {canValidate && !result?.success && (
                  <>
                    <Button onClick={handleValidate} disabled={validating}
                      className="w-full h-12 bg-green-500 text-white hover:bg-green-600 rounded-xl text-base font-medium"
                      data-testid="resp-validate-btn">
                      <Check size={20} className="mr-2" /> {validating ? 'Validation...' : 'Valider et encaisser'}
                    </Button>
                    {bon.statut === 'CREE' && (
                      <Button onClick={handleRemettre} variant="ghost"
                        className="w-full text-orange-400 hover:bg-orange-400/10 text-sm"
                        data-testid="resp-remettre-btn">
                        Marquer comme remis au client
                      </Button>
                    )}
                  </>
                )}
                {bon.statut === 'UTILISE' && !result && (
                  <div className="flex items-center justify-center gap-2 p-3 rounded-xl bg-orange-400/10 text-orange-400 text-sm">
                    <AlertTriangle size={16} />
                    <span>Déjà utilisé{bon.date_utilisation ? ` le ${bon.date_utilisation}` : ''}</span>
                  </div>
                )}
                {bon.statut === 'EXPIRE' && !result && (
                  <div className="flex items-center justify-center gap-2 p-3 rounded-xl bg-red-400/10 text-red-400 text-sm">
                    <XCircle size={16} /> Ce bon est expiré
                  </div>
                )}
                <Button onClick={resetScan} variant="ghost" className="w-full text-[#5A6478] hover:text-white text-sm" data-testid="resp-scan-another">
                  <Camera size={16} className="mr-2" /> Scanner un autre bon
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <QrScannerModal open={showScanner} onClose={() => setShowScanner(false)} onScan={handleScan} />
      <p className="mt-auto pt-8 text-[10px] text-[#3A4560]">Le Mas — Responsable</p>
    </div>
  );
}
